import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GitlabApiRequestTokenResponse } from './interfaces/gitlab-api-request-token-response.interface';
import { GITLAB_API_CONFIG } from './gitlab-api-config.constants';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GitlabApiAuthService {
  readonly #http = inject(HttpClient);

  #token: GitlabApiRequestTokenResponse | null = null;

  constructor() {
    this.#loadToken();
  }

  get isAuthenticated() {
    return this.#token !== null;
  }

  get accessToken() {
    return this.#token?.access_token;
  }

  authenticate() {
    window.location.href = GITLAB_API_CONFIG.AUTH_REQUEST_URI;
  }

  requestToken(code: string) {
    return this.#http
      .post<GitlabApiRequestTokenResponse>(GITLAB_API_CONFIG.TOKEN_ENDPOINT, {
        client_id: GITLAB_API_CONFIG.APP_ID,
        client_secret: GITLAB_API_CONFIG.SECRET,
        code,
        grant_type: 'authorization_code',
        redirect_uri: GITLAB_API_CONFIG.REDIRECT_URI,
      })
      .pipe(
        tap((res) => {
          this.#setToken(res);
        })
      );
  }

  refreshToken() {
    return this.#http
      .post<GitlabApiRequestTokenResponse>(GITLAB_API_CONFIG.TOKEN_ENDPOINT, {
        client_id: GITLAB_API_CONFIG.APP_ID,
        client_secret: GITLAB_API_CONFIG.SECRET,
        refresh_token: this.#token!.refresh_token,
        grant_type: 'refresh_token',
        redirect_uri: GITLAB_API_CONFIG.REDIRECT_URI,
      })
      .pipe(
        map((res) => {
          this.#setToken(res);
        })
      );
  }

  isTokenExpired() {
    if (this.#token === null) {
      return false;
    }

    return (
      this.#token.created_at + this.#token.expires_in <
      new Date().getTime() / 1000
    );
  }

  #loadToken() {
    const localStorageToken = window.localStorage.getItem(
      GITLAB_API_CONFIG.LOCALSTORAGE_KEY
    );

    if (localStorageToken !== null) {
      try {
        this.#setToken(JSON.parse(localStorageToken));
      } catch (e) {
        this.#setToken(null);
      }
    }
  }

  #setToken(token: GitlabApiRequestTokenResponse | null) {
    if (token === null) {
      window.localStorage.removeItem(GITLAB_API_CONFIG.LOCALSTORAGE_KEY);
    } else {
      window.localStorage.setItem(
        GITLAB_API_CONFIG.LOCALSTORAGE_KEY,
        JSON.stringify(token)
      );
    }

    this.#token = token;
  }
}
